import React, {
  useState, useEffect, useMemo, useCallback,
} from 'react';
import PropTypes from 'prop-types';
import debounce from 'lodash/debounce';
import { message } from 'antd';
import * as api from 'util/api';
import { getCaseTypeLabel } from 'util/sku-formatter';
import Cin7SkuMatcher from '../Cin7SkuMatcher/Cin7SkuMatcher';

function unitTypeDisplayLabel(sku) {
  const u = String(sku?.unitType ?? sku?.unit_type ?? '');
  if (!u) return '';
  if (/^\d{2,5}$/.test(u)) {
    const n = parseInt(u, 10);
    if (n >= 10000) return '';
    return `${n}ml`;
  }
  return u;
}

/**
 * Cin7 SKU matcher that persists to the product SKU row immediately (same API as Product Database).
 */
const SkuCin7InlineEditor = ({
  productId,
  skuKey,
  sku,
  parentProductName,
  onSaved,
  compact,
  fullWidth,
  searchOnly,
  sharedCachedProducts,
}) => {
  const cin7FromServer = sku?.cin7Sku ?? sku?.cin7_sku ?? '';
  const [localCin7, setLocalCin7] = useState(cin7FromServer);

  useEffect(() => {
    setLocalCin7(cin7FromServer);
  }, [cin7FromServer, skuKey]);

  const persist = useCallback(async (payload) => {
    try {
      await api.updateProductSkuStrict(productId, skuKey, payload);
      message.success('Cin7 link saved');
      onSaved?.();
    } catch (e) {
      message.error('Failed to save Cin7 SKU');
    }
  }, [productId, skuKey, onSaved]);

  const debouncedPersistCin7Only = useMemo(
    () => debounce((next) => {
      const trimmed = (next ?? '').trim();
      if (!trimmed) {
        persist({ cin7Sku: null, dearCode: null });
      } else {
        persist({ cin7Sku: trimmed });
      }
    }, 650),
    [persist],
  );

  useEffect(() => () => debouncedPersistCin7Only.cancel(), [debouncedPersistCin7Only]);

  const ct = sku?.containerType ?? sku?.container_type ?? '';
  const ut = sku?.unitType ?? sku?.unit_type ?? '';
  const pt = sku?.packType ?? sku?.pack_type ?? '';
  const cs = sku?.caseType ?? sku?.case_type ?? '';
  const nm = sku?.name ?? '';
  const caseTypeLabel = getCaseTypeLabel(cs || '', pt || '');
  const unitTypeLabel = unitTypeDisplayLabel(sku);

  const handleSelectMatch = (cin7Product) => {
    debouncedPersistCin7Only.cancel();
    const raw = cin7Product?.cin7Sku ?? cin7Product?.cin7_sku ?? cin7Product?.SKU ?? '';
    const dearCode = cin7Product?.cin7Id ?? cin7Product?.cin7_id ?? cin7Product?.ID ?? null;
    let cin7Str = null;
    if (raw !== '' && raw != null) {
      cin7Str = typeof raw === 'string' ? raw : String(raw);
    }
    setLocalCin7(cin7Str ?? '');
    persist({
      cin7Sku: cin7Str,
      dearCode: dearCode ?? null,
    });
  };

  if (!productId || !skuKey) {
    return null;
  }

  let boxStyle = { minWidth: 260, maxWidth: 340 };
  if (fullWidth) boxStyle = { width: '100%', minWidth: 0, maxWidth: '100%' };
  else if (compact) boxStyle = { minWidth: 200, maxWidth: 280 };

  return (
    <div style={boxStyle}>
      <Cin7SkuMatcher
        value={localCin7}
        compact={compact}
        searchOnly={searchOnly}
        sharedCachedProducts={sharedCachedProducts}
        onChange={(e) => {
          const v = e.target.value;
          setLocalCin7(v);
          debouncedPersistCin7Only(v);
        }}
        onSelectMatch={handleSelectMatch}
        parentProductName={parentProductName || ''}
        matrixSkuName={nm}
        containerType={ct}
        unitType={ut}
        packType={pt}
        caseTypeLabel={caseTypeLabel}
        unitTypeLabel={unitTypeLabel}
      />
    </div>
  );
};

SkuCin7InlineEditor.propTypes = {
  productId: PropTypes.number,
  skuKey: PropTypes.number,
  sku: PropTypes.shape({
    cin7Sku: PropTypes.string,
    cin7_sku: PropTypes.string,
    name: PropTypes.string,
    containerType: PropTypes.string,
    container_type: PropTypes.string,
    unitType: PropTypes.string,
    unit_type: PropTypes.string,
    packType: PropTypes.string,
    pack_type: PropTypes.string,
    caseType: PropTypes.string,
    case_type: PropTypes.string,
  }),
  parentProductName: PropTypes.string,
  onSaved: PropTypes.func,
  compact: PropTypes.bool,
  fullWidth: PropTypes.bool,
  searchOnly: PropTypes.bool,
  sharedCachedProducts: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.oneOf([null]),
  ]),
};

SkuCin7InlineEditor.defaultProps = {
  productId: null,
  skuKey: null,
  sku: null,
  parentProductName: '',
  onSaved: undefined,
  compact: false,
  fullWidth: false,
  searchOnly: false,
  sharedCachedProducts: undefined,
};

export default SkuCin7InlineEditor;
